import React from 'react'
import { Link } from 'react-router-dom'

import { THEME_COLOR_GROUP_GREEN, THEME_COLOR_GROUP_YELLOW } from '../app/support/theme'
import { listToMap } from '../common'

import futureRoadmap from '../assets/future-roadmap.svg'

import welcomeRoadmapImage from '../assets/welcome-roadmap.svg'
import trackGoalProgressImage from '../assets/track-goal-progress.svg'
import readyToStartGoalImage from '../assets/ready-to-start-goal.svg'

import {
  GOAL_STATUSES,
  GOAL_STATUS_ACHIEVED,
  GOAL_STATUS_ACTIVE,
  GOAL_STATUS_DRAFT,
  GOAL_STATUS_FUTURE,
  GOAL_TYPE_ADD_ON_SERVICE,
  GOAL_TYPE_LEGACY,
  GOAL_TYPE_STANDARD,
  GOAL_TYPE_SUPER_GOAL,
  GOAL_TYPE_FOUNDATIONAL,
  GOAL_TYPE_ANNUAL,
  GOAL_TYPE_FIRST,
  GOAL_TYPE_LEGACY_ADD_ON_SERVICE
} from '../constants'

import EmptyPlanningCategory from './support/EmptyPlanningCategory'

const GOAL_PREVIEW_ID_PATTERN = /^goal-preview-(\d+)$/

const GOAL_TYPE_SPECIFICATIONS = [
  { label: 'Add-on Service', value: GOAL_TYPE_ADD_ON_SERVICE, color: THEME_COLOR_GROUP_YELLOW.Y200, teamEnabled: true },

  // Per UND-3828, there was a need to restore goal chats to pre-existing add-on service goals but not to newer ones.
  // To make the distinction, this goal type was defined and the pre-existing goals were marked as such.
  {
    label: 'Add-on Service',
    value: GOAL_TYPE_LEGACY_ADD_ON_SERVICE,
    color: THEME_COLOR_GROUP_YELLOW.Y200,
    teamEnabled: true,
    chatEnabled: true
  },

  { label: 'Annual', value: GOAL_TYPE_ANNUAL, teamEnabled: true },
  { label: 'First', value: GOAL_TYPE_FIRST, teamEnabled: true },
  { label: 'Foundational', value: GOAL_TYPE_FOUNDATIONAL, teamEnabled: true },

  {
    label: 'Legacy',
    value: GOAL_TYPE_LEGACY,
    color: THEME_COLOR_GROUP_GREEN.G100,
    cardLabel: '',
    teamEnabled: true,
    chatEnabled: true
  },

  {
    label: 'Standard',
    value: GOAL_TYPE_STANDARD,
    color: THEME_COLOR_GROUP_GREEN.G100,
    cardLabel: '',
    teamEnabled: true
  },

  { label: 'Super Goal', value: GOAL_TYPE_SUPER_GOAL, teamEnabled: true }
]

const GOAL_TYPE_SPECIFICATIONS_MAP = listToMap(GOAL_TYPE_SPECIFICATIONS, 'value')

const createGoalCategory = ({ status, title, ...rest }) => {
  const key = status
  return {
    key,
    title: title || GOAL_STATUSES[key].label,
    styleSuffix: key,
    status,
    ...rest
  }
}

const LOCKED_GOAL_CATEGORIES = [
  createGoalCategory({
    status: GOAL_STATUS_FUTURE,
    expandGoalsInitially: false,
    lazyLoad: false,
    draft: false,
    emptyTitle: 'For later goals on the way…',
    emptyMessage: (
      <span>
        Future goals show here—your Navigator will help you set them up. Have a goal in mind? Add it as a{' '}
        <Link to="/vision">need or want</Link>.
      </span>
    )
  })
]

const GOAL_CATEGORIES = [
  createGoalCategory({
    status: GOAL_STATUS_ACTIVE,
    expandGoalsInitially: true,
    lazyLoad: false,
    draft: false,
    emptyTitle: 'Active goals on the way…',
    emptyMessage: (
      <span>
        Your Navigator will help you set up and activate your goals. Have a goal in mind? Add it as a{' '}
        <Link to="/vision">need or want</Link>.
      </span>
    )
  }),

  ...LOCKED_GOAL_CATEGORIES,

  createGoalCategory({
    status: GOAL_STATUS_ACHIEVED,
    expandGoalsInitially: false,
    lazyLoad: true,
    draft: false,
    emptyTitle: 'Achieved goals on the way…',
    emptyMessage: <span>When you complete a goal (and you will), you’ll see your success here.</span>
  }),

  createGoalCategory({
    status: GOAL_STATUS_DRAFT,
    expandGoalsInitially: false,
    lazyLoad: true,
    draft: true,
    emptyTitle: 'No draft Goals',
    navigatorOnly: true
  })
]

const PLANNING_CATEGORY_DRAFT = createGoalCategory({
  status: GOAL_STATUS_DRAFT,
  expandGoalsInitially: true,
  lazyLoad: false,
  draft: true,
  emptyTitle: 'User has no draft goals',
  EmptyComponent: EmptyPlanningCategory,
  navigatorOnly: true
})

const PLANNING_CATEGORY_ACTIVE = createGoalCategory({
  status: GOAL_STATUS_ACTIVE,
  title: 'Working on it',
  expandGoalsInitially: true,
  lazyLoad: false,
  draft: false,
  emptyTitle: 'You’re not working on any goals right now.',
  emptyMessage: (
    <span>
      Your Navigator will help you set up and activate your goals. Have an idea in mind? Add it as a{' '}
      <Link to="../checklist">task</Link>.
    </span>
  )
})

const PLANNING_CATEGORY_FUTURE = createGoalCategory({
  status: GOAL_STATUS_FUTURE,
  title: 'Future Roadmap',
  expandGoalsInitially: false,
  lazyLoad: false,
  draft: false,
  hideIfEmpty: true,
  backgroundImagePath: futureRoadmap,
  showToggleButton: true,
  infoIconTooltipContent: 'Goals that you will accomplish in the years to come',
  hideSubscript: true
})

const PLANNING_CATEGORY_ACHIEVED = createGoalCategory({
  status: GOAL_STATUS_ACHIEVED,
  title: 'Completed',
  expandGoalsInitially: false,
  lazyLoad: true,
  draft: false,
  hideIfEmpty: true,
  emptyTitle: 'User has no complete goals'
})

const PREREQUISITE_LABEL = {
  childrensServices: 'Requires California Children’s Services (CCS) program enrollment',
  deemingWaiver: 'Requires a Regional Center institutional deeming waiver',
  departmentOfRehab: 'Requires Department of Rehab (DOR) enrollment',
  documentedDiagnosis: 'Requires a documented medical diagnosis',
  earlyIntervention: 'Early Intervention program services',
  educationLevel: 'Applies to the following level of Education',
  ihssProgram: 'Requires IHSS program enrollment',
  mediCal: 'Requires Medi-Cal',
  privateInsurance: 'Requires private insurance',
  regionalCenter: 'Requires Regional Center',
  requires504: 'Requires a 504 Plan',
  requiresIEP: 'Requires an IEP',
  selfDetermination: 'Self-Determination program enrollment',
  traditionalFunding: 'Requires a traditional funding route'
}

const ROADMAP_STEPS = [
  {
    title: 'Welcome to your Roadmap!',
    description:
      'A Roadmap is a suggested plan of goals to reach, over time. We’ll suggest ages for each goal, but they can be moved to fit your needs and meet you where you are.',
    image: welcomeRoadmapImage
  },
  {
    title: 'Track real progress in a goal workspace',
    description:
      'Each goal is equipped with a dedicated workspace. With a membership, you’ll have full access to step-by-step guidance, and actionable tasks.',
    image: trackGoalProgressImage
  },
  {
    title: 'Achieve your goals!',
    description:
      'Start on a goal, and access all the tools needed to achieve it. After you accomplish a goal, we’ll suggest new ones to reach new milestones.',
    image: readyToStartGoalImage
  }
]

const BASE_PLANNING_CATEGORIES = [PLANNING_CATEGORY_ACTIVE, PLANNING_CATEGORY_FUTURE, PLANNING_CATEGORY_ACHIEVED]

const ALL_PLANNING_CATEGORIES = [PLANNING_CATEGORY_DRAFT, ...BASE_PLANNING_CATEGORIES]

const UNCHECKED_DEFAULT_LIMIT = 500
const CHECKED_DEFAULT_LIMIT = 500

const CHECKLIST_HIGHLIGHT_PARAM = 'highlightId'

const CHECKLIST_ITEM_PRIORITY_HIGH = 'high'
const CHECKLIST_ITEM_PRIORITY_LOW = 'low'

const ALL_GOALS_LABEL = 'All goals'
const ALL_GOALS_VALUE = 'all-goals'

const ROADMAP_TEMPLATES_LABEL = 'Roadmap templates'
const ROADMAP_TEMPLATES_VALUE = 'roadmap-templates'

const GOAL_ACTION_DELETE = 'deleted'
const GOAL_ACTION_UPDATE = 'updateGoal'
const GOAL_ACTION_STATUS_UPDATE = 'updateGoalStatus'
const GOAL_ACTION_DRAFT = 'draft'

const GOAL_ACTION_SNACKBAR_MESSAGE = {
  [GOAL_ACTION_DRAFT]: 'Goal marked draft.',
  [GOAL_ACTION_UPDATE]: 'Goal is updated',
  [GOAL_ACTION_DELETE]: 'Goal is deleted'
}

// The due date type affects various copy/labels in the dialog.
//
// Although there appears to be a lot of overlap among these variants, the overlap itself isn’t
// always straightforward and distilling the logic can get complicated, so we choose to make
// the variants completely customizable, trading off the redundancy for clarity.
//
// One exception: the snackbar messages are pretty consistent, so we have a convenience function.
// But instead of computing on the fly, we precompute and store the whole string, so that we can
// still do pinpoint customization if needed.
const DUE_DATE_ADD = 'add'
const DUE_DATE_UPDATE = 'update'
const DUE_DATE_REMOVE = 'remove'

/**
 * This is the aforementioned exception.
 *
 * @param {string} noun the due date whose message this will be about
 * @param {string} action the change that was made to the due date (see constants above)
 * @returns the snackbar message for the noun and action
 */
const dueDateSnackbarMessage = (noun, action) =>
  `Your ${noun} was ${
    action === DUE_DATE_ADD ? 'added to' : action === DUE_DATE_REMOVE ? 'removed from' : 'updated in'
  } your events`

const AUDIT_DUE_DATE_TRACK_TYPE = {
  iepMeetingDate: 'IEP meeting',
  ippMeetingDate: 'IPP meeting',
  mediCalRenewalDate: 'Medi-Cal renewal',
  ihssRenewalDate: 'IHSS visit'
}

const DUE_DATE_TYPE_SPECIFICATIONS = {
  mediCal: {
    addCta: 'Add Medi-Cal Renewal',
    chipPrompt: 'Add Medi-Cal renewal',
    confirmCta: 'Confirm Medi-Cal Renewal',
    confirmQuestionPrompt: 'Confirm your Medi-Cal Renewal',
    deleteConfirmationPrompt: 'Are you sure you want to remove the Medi-Cal renewal date?',
    noun: 'Medi-Cal renewal',
    questionPrompt: 'When is your Medi-Cal renewal date?',
    snackbarAdded: dueDateSnackbarMessage('Medi-Cal renewal', DUE_DATE_ADD),
    snackbarUpdated: dueDateSnackbarMessage('Medi-Cal renewal', DUE_DATE_UPDATE),
    snackbarRemoved: dueDateSnackbarMessage('Medi-Cal renewal', DUE_DATE_REMOVE)
  },

  caInHomeSupportiveServices: {
    addCta: 'Add IHSS Visit',
    chipPrompt: 'Add IHSS visit',
    confirmCta: 'Confirm IHSS Visit',
    confirmQuestionPrompt: 'Confirm your IHSS Visit',
    deleteConfirmationPrompt: 'Are you sure you want to remove the IHSS visit date?',
    noun: 'IHSS visit',
    questionPrompt: 'When is your IHSS visit date?',
    snackbarAdded: dueDateSnackbarMessage('IHSS visit', DUE_DATE_ADD),
    snackbarUpdated: dueDateSnackbarMessage('IHSS visit', DUE_DATE_UPDATE),
    snackbarRemoved: dueDateSnackbarMessage('IHSS visit', DUE_DATE_REMOVE)
  },

  caRegionalCenter: {
    addCta: 'Add IPP Meeting',
    chipPrompt: 'Add IPP meeting',
    confirmCta: 'Confirm IPP Meeting',
    confirmQuestionPrompt: 'Confirm your IPP Meeting',
    deleteConfirmationPrompt: 'Are you sure you want to remove the IPP meeting date?',
    noun: 'IPP meeting',
    questionPrompt: 'When is your IPP meeting date?',
    snackbarAdded: dueDateSnackbarMessage('IPP meeting', DUE_DATE_ADD),
    snackbarUpdated: dueDateSnackbarMessage('IPP meeting', DUE_DATE_UPDATE),
    snackbarRemoved: dueDateSnackbarMessage('IPP meeting', DUE_DATE_REMOVE)
  },

  'school:504': {
    addCta: 'Add 504 Meeting',
    chipPrompt: 'Add 504 Meeting',
    confirmCta: 'Confirm 504 Meeting',
    confirmQuestionPrompt: 'Confirm your 504 Meeting',
    deleteConfirmationPrompt: 'Are you sure you want to remove the 504 meeting date?',
    noun: '504 meeting',
    questionPrompt: 'When is your 504 meeting date?',
    snackbarAdded: dueDateSnackbarMessage('504 meeting', DUE_DATE_ADD),
    snackbarUpdated: dueDateSnackbarMessage('504 meeting', DUE_DATE_UPDATE),
    snackbarRemoved: dueDateSnackbarMessage('504 meeting', DUE_DATE_REMOVE)
  },

  'school:iep': {
    addCta: 'Add IEP Meeting',
    chipPrompt: 'Add IEP Meeting',
    confirmCta: 'Confirm IEP Meeting',
    confirmQuestionPrompt: 'Confirm your IEP Meeting',
    deleteConfirmationPrompt: 'Are you sure you want to remove the IEP meeting date?',
    noun: 'IEP meeting',
    questionPrompt: 'When is your IEP meeting date?',
    snackbarAdded: dueDateSnackbarMessage('IEP meeting', DUE_DATE_ADD),
    snackbarUpdated: dueDateSnackbarMessage('IEP meeting', DUE_DATE_UPDATE),
    snackbarRemoved: dueDateSnackbarMessage('IEP meeting', DUE_DATE_REMOVE)
  }
}

// And, for a generic due date, we have:
const DUE_DATE_DEFAULT = {
  addCta: 'Add Target Date',
  chipPrompt: 'Add target date',
  confirmCta: 'Confirm Target Date',
  confirmQuestionPrompt: 'Confirm your Target Date',
  deleteConfirmationPrompt: 'Are you sure you want to remove the goal target date?',
  noun: 'goal target',
  questionPrompt: 'When is your goal target date?',
  snackbarAdded: dueDateSnackbarMessage('goal target', DUE_DATE_ADD),
  snackbarUpdated: dueDateSnackbarMessage('goal target', DUE_DATE_UPDATE),
  snackbarRemoved: dueDateSnackbarMessage('goal target', DUE_DATE_REMOVE)
}

const ROADMAP_MAX_DISPLAYABLE_AGE = 22

// This is the only roadmap category with a fixed key. All other categories
// are derived from ages.
const ROADMAP_CATEGORY_COMPLETED = 'completedGoal'
const ROADMAP_CATEGORY_BIRTH_LABEL = 'Birth'
const ROADMAP_CATEGORY_MAX_LABEL = `Over ${ROADMAP_MAX_DISPLAYABLE_AGE}`

const ADD_GOALS_LIST = [
  {
    label: ALL_GOALS_LABEL,
    value: ALL_GOALS_VALUE
  },
  {
    label: ROADMAP_TEMPLATES_LABEL,
    value: ROADMAP_TEMPLATES_VALUE
  }
]

const INTENT_GET_ROADMAP = 'getRoadmap'
const INTENT_SCROLL_TO_BOTTOM = 'scrollToBottom' // Used mainly to show a newly-created recurring goal.

const READ_ONLY_MESSAGE = 'This task is derived from a goal and cannot be edited.'

// Even though these constants pertain to goal subviews, they are defined here in order
// to avoid import/reference/chunk order issues due to the dependency chain of the
// modules that import them.
const GOAL_SUBVIEW_ABOUT = 'aboutTheGoal'
const GOAL_SUBVIEW_ADD_TO_ROADMAP = 'addToRoadmap'
const GOAL_SUBVIEW_TASKS_NOTES_DOCS = 'tasksNotesDocuments'
const GOAL_SUBVIEW_DOCS_NOTES = 'documentsNotes'
const GOAL_SUBVIEW_DOCS = 'documents'
const GOAL_SUBVIEW_IEP = 'iep'
const GOAL_SUBVIEW_IEP_CTA = 'iep'
const GOAL_SUBVIEW_IPP = 'ipp'
const GOAL_SUBVIEW_504 = '504'
const GOAL_SUBVIEW_NOTES = 'notes'
const GOAL_SUBVIEW_TASKS = 'tasks'
const GOAL_SUBVIEW_TRIAL_CTA = 'trialCta'
const GOAL_SUBVIEW_START_TOOLBAR = 'startGoalToolbar'
const GOAL_SUBVIEW_ACTIVE_TOOLBAR = 'activeGoalToolbar'
const GOAL_SUBVIEW_RESTART_CTA = 'restartMembershipCta'
const GOAL_SUBVIEW_COMPLETE_GOAL_TOOLBAR = 'completeGoalToolbar'
const GOAL_SUBVIEW_STEPS = 'steps'
const GOAL_SUBVIEW_RESOURCES = 'resources'
const GOAL_SUBVIEW_COMPLETED_TOOLBAR = 'completedGoalToolbar'
const GOAL_SUBVIEW_PREVIOUS_RECURRING_GOAL_TOOLBAR = 'currentDescendant'

export {
  GOAL_PREVIEW_ID_PATTERN,
  GOAL_TYPE_SPECIFICATIONS,
  GOAL_TYPE_SPECIFICATIONS_MAP,
  GOAL_CATEGORIES,
  PLANNING_CATEGORY_ACTIVE,
  PLANNING_CATEGORY_FUTURE,
  PLANNING_CATEGORY_ACHIEVED,
  PREREQUISITE_LABEL,
  BASE_PLANNING_CATEGORIES,
  ALL_PLANNING_CATEGORIES,
  UNCHECKED_DEFAULT_LIMIT,
  CHECKED_DEFAULT_LIMIT,
  CHECKLIST_HIGHLIGHT_PARAM,
  CHECKLIST_ITEM_PRIORITY_HIGH,
  CHECKLIST_ITEM_PRIORITY_LOW,
  LOCKED_GOAL_CATEGORIES,
  ADD_GOALS_LIST,
  ALL_GOALS_VALUE,
  DUE_DATE_TYPE_SPECIFICATIONS,
  DUE_DATE_DEFAULT,
  ROADMAP_TEMPLATES_VALUE,
  GOAL_ACTION_DELETE,
  GOAL_ACTION_UPDATE,
  GOAL_ACTION_STATUS_UPDATE,
  GOAL_ACTION_DRAFT,
  GOAL_ACTION_SNACKBAR_MESSAGE,
  GOAL_SUBVIEW_ABOUT,
  GOAL_SUBVIEW_ADD_TO_ROADMAP,
  GOAL_SUBVIEW_TASKS_NOTES_DOCS,
  GOAL_SUBVIEW_DOCS_NOTES,
  GOAL_SUBVIEW_DOCS,
  GOAL_SUBVIEW_IEP,
  GOAL_SUBVIEW_IEP_CTA,
  GOAL_SUBVIEW_IPP,
  GOAL_SUBVIEW_504,
  GOAL_SUBVIEW_PREVIOUS_RECURRING_GOAL_TOOLBAR,
  GOAL_SUBVIEW_NOTES,
  GOAL_SUBVIEW_TASKS,
  GOAL_SUBVIEW_TRIAL_CTA,
  GOAL_SUBVIEW_START_TOOLBAR,
  GOAL_SUBVIEW_ACTIVE_TOOLBAR,
  GOAL_SUBVIEW_RESTART_CTA,
  GOAL_SUBVIEW_COMPLETE_GOAL_TOOLBAR,
  GOAL_SUBVIEW_COMPLETED_TOOLBAR,
  GOAL_SUBVIEW_STEPS,
  GOAL_SUBVIEW_RESOURCES,
  READ_ONLY_MESSAGE,
  ROADMAP_CATEGORY_COMPLETED,
  ROADMAP_CATEGORY_BIRTH_LABEL,
  ROADMAP_CATEGORY_MAX_LABEL,
  ROADMAP_MAX_DISPLAYABLE_AGE,
  ROADMAP_STEPS,
  INTENT_GET_ROADMAP,
  INTENT_SCROLL_TO_BOTTOM,
  AUDIT_DUE_DATE_TRACK_TYPE
}
